import React, { useEffect, useState } from 'react';
import { getProducts, updateProductStatus, deleteProduct } from '../lib/db';
import { SearchBar } from './SearchBar';
import { ViewToggle } from './ViewToggle';
import { ProductList } from './ProductList';
import { EditProductModal } from './EditProductModal';
import { ConfirmationModal } from './ConfirmationModal';
import { PullToRefresh } from './PullToRefresh';
import { sendToWooCommerce } from '../lib/woocommerce';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { subscribeToProducts } from '../lib/supabase';
import toast from 'react-hot-toast';

export function OrderList() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState<{
    show: boolean;
    type: 'restore' | 'delete' | 'woocommerce';
    productId: number | null;
    title: string;
    message: string;
    product?: any;
  }>({
    show: false,
    type: 'restore',
    productId: null,
    title: '',
    message: ''
  });

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await getProducts('pending');
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    const subscription = subscribeToProducts(loadOrders);
    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const handleDelete = (id: number) => {
    setConfirmAction({
      show: true,
      type: 'delete',
      productId: id,
      title: 'Confirmer la suppression',
      message: 'Voulez-vous vraiment supprimer ce produit ?'
    });
  };

  const showConfirmRestore = (id: number) => {
    setConfirmAction({
      show: true,
      type: 'restore',
      productId: id,
      title: 'Retour au Live',
      message: 'Voulez-vous vraiment remettre ce produit en Live ?'
    });
  };

  const showConfirmWooCommerce = (product: any) => {
    setConfirmAction({
      show: true,
      type: 'woocommerce',
      productId: product.id,
      product: product,
      title: 'Envoyer vers WooCommerce',
      message: 'Voulez-vous vraiment envoyer ce produit vers WooCommerce ?'
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction.productId) return;

    try {
      switch (confirmAction.type) {
        case 'restore':
          await updateProductStatus(confirmAction.productId, 'draft');
          toast.success('Produit remis en Live');
          break;
        case 'delete':
          await deleteProduct(confirmAction.productId);
          toast.success('Produit supprimé');
          break;
        case 'woocommerce':
          if (confirmAction.product) {
            await sendToWooCommerce(confirmAction.product);
            await updateProductStatus(confirmAction.productId, 'completed');
            toast.success('Produit envoyé vers WooCommerce avec succès');
          }
          break;
      }
      await loadOrders();
    } catch (error) {
      console.error('Une erreur est survenue:', error);
      toast.error('Une erreur est survenue');
    }
  };

  return (
    <PullToRefresh onRefresh={loadOrders}>
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold text-gray-900">Liste des commandes</h2>
                <button
                  onClick={() => navigate('/')}
                  className="text-blue-600 hover:text-blue-800"
                  title="Retour au Live"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
              </div>
              <div className="flex space-x-4">
                <ViewToggle view={view} onViewChange={setView} />
              </div>
            </div>
            <div className="mt-4">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Recherche par numéro client"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Aucune commande en cours
          </div>
        ) : (
          <ProductList
            products={orders}
            view={view}
            searchTerm={searchTerm}
            onEdit={setEditingProduct}
            onStatusChange={showConfirmRestore}
            onDelete={handleDelete}
            onWooCommerce={showConfirmWooCommerce}
            actionIcon="restore"
          />
        )}

        {editingProduct && (
          <EditProductModal
            product={editingProduct}
            isOpen={true}
            onClose={() => setEditingProduct(null)}
            onUpdate={loadOrders}
            showStatusChange={true}
            currentStatus="pending"
            onStatusChange={(id) => {
              updateProductStatus(id, 'draft');
              loadOrders();
              setEditingProduct(null);
            }}
          />
        )}

        <ConfirmationModal
          isOpen={confirmAction.show}
          onClose={() => setConfirmAction({ ...confirmAction, show: false })}
          onConfirm={handleConfirmAction}
          title={confirmAction.title}
          message={confirmAction.message}
        />
      </div>
    </PullToRefresh>
  );
}